import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import LoadingOverlay from '../../LoadingOverlay';
import { useStores } from '../../stores/useStores';
import AddTrappingRunModal from './AddTrappingRunModal';
import TrappingRun from './TrappingRun';
import TrappingRunsTable from './TrappingRunsTable';

function TrappingRuns(props) {
  const { authenticationStore } = useStores();

  const [showAddTrappingRunModal, setShowAddTrappingRunModal] = useState(false);
  const [trappingRuns, setTrappingRuns] = useState([]);
  const [loadingTrappingRuns, setLoadingTrappingRuns] = useState(false);
  const [loadingPropertyRoles, setLoadingPropertyRoles] = useState(false);
  const [propertyRoles, setPropertyRoles] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);

  const customAddTrappingRunOptions = props.customOptions?.addTrappingRun;
  const customEditTrappingRunOptions = props.customOptions?.editTrappingRun;
  const customTableOptions = props.customOptions?.table;

  /**
   * Load the following when the bearer token is updated:
   *  - property roles
   *  - users (Used by propery group permissions)
   */
  useEffect(() => {
    // Update authentication store
    authenticationStore.setSelectedDatabase(props.selectedDatabase);
    authenticationStore.setBearerToken(props.bearerToken);
    authenticationStore.setRefreshBearerToken(props.refreshBearerToken);

    if (!props.selectedDatabase) return;

    // Load trapping runs
    setLoadingTrappingRuns(true);
    fetch(`${process.env.REACT_APP_TRAPLOG_API_URL}api/trapping-runs`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${authenticationStore.bearerToken}`,
        SeasonalDatabase: props.selectedDatabase
      }
    })
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) authenticationStore.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          setTrappingRuns(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        setTrappingRuns([]);
      })
      .finally(() => {
        setLoadingTrappingRuns(false);
      });

    // Property roles
    setLoadingPropertyRoles(true);
    fetch(`${process.env.REACT_APP_PROPERTIES_API_URL}api/property-roles`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + authenticationStore.bearerToken,
        SeasonalDatabase: props.selectedDatabase
      }
    })
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) authenticationStore.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          setPropertyRoles(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        setPropertyRoles([]);
      })
      .finally(() => {
        setLoadingPropertyRoles(false);
      });

    // Users
    setLoadingUsers(true);
    fetch(`${process.env.REACT_APP_AUTH_API_URL}api/users`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + authenticationStore.bearerToken,
        service: props.authService
      }
    })
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) authenticationStore.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          setUsers(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        setUsers([]);
      })
      .finally(() => {
        setLoadingUsers(false);
      });
  }, [props.selectedDatabase, props.bearerToken, props.refreshBearerToken]);

  /**
   * Returns the selected trapping run model from the list of trapping runs.
   *
   * @returns {object|null}
   */
  const selectedTrappingRun = useMemo(
    () =>
      trappingRuns.find(
        (trappingRun) => trappingRun.id == props.selectedTrappingRunId
      ),
    [trappingRuns, props.selectedTrappingRunId]
  );

  /**
   * If the supplied trapping run exists in the list of trapping runs
   * (found using id) then update it.
   *
   * @param {object} trappingRun
   */
  const updateTrappingRun = (trappingRun, isDelete) => {
    const i = trappingRuns.findIndex((t) => t.id === trappingRun.id);
    if (i === -1) return;
    if (isDelete) {
      const newTrappingRuns = [...trappingRuns];
      newTrappingRuns.splice(i, 1);
      setTrappingRuns(newTrappingRuns);
    } else {
      const newTrappingRuns = [...trappingRuns];
      newTrappingRuns[i] = trappingRun;
      setTrappingRuns(newTrappingRuns);
    }
  };

  /**
   * If the supplied trapping run does not exists in the list of trapping
   * runs (found using id) then add it in.
   *
   * @param {object} trappingRun
   */
  const addTrappingRun = (trappingRun) => {
    const i = trappingRuns.findIndex((t) => t.id === trappingRun.id);
    if (i < 0) {
      const newTrappingRuns = [...trappingRuns];
      newTrappingRuns.push(trappingRun);
      setTrappingRuns(newTrappingRuns);
    }
  };

  /**
   * Sends an http request to the properties API to delete the property
   * group and removes it from the property groups state array.
   *
   * @param {object} propertyGroupId
   */
  const deleteTrappingRun = (trappingRun) => {
    return fetch(
      `${process.env.REACT_APP_PROPERTIES_API_URL}api/property-groups/${trappingRun.property_group.id}?with[]=properties&with[]=users.propertyRole&append[]=userProperty`,
      {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + authenticationStore.bearerToken,
          SeasonalDatabase: authenticationStore.selectedDatabase
        }
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) authenticationStore.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          /*
           * Property groups use soft deletion, so update the property group
           * in the property groups state array with the new deleted_at field.
           */
          trappingRun.property_group = data;
          updateTrappingRun(trappingRun);
        }
      });
  };

  return loadingTrappingRuns || loadingPropertyRoles || loadingUsers ? (
    <div className='h-100'>
      <LoadingOverlay hideOpacity />
    </div>
  ) : selectedTrappingRun ? (
    <TrappingRun
      permissionsCard={props.permissionsCard}
      selectedDatabase={props.selectedDatabase}
      selectedTrappingRun={selectedTrappingRun}
      update={updateTrappingRun}
      close={() => props.changeSelectedTrappingRunId(null)}
      propertyRoles={propertyRoles}
      users={users}
      history={props.history}
      customOptions={customEditTrappingRunOptions}
    />
  ) : (
    <Fragment>
      <Row>
        <Col>
          <Button
            variant='link'
            className='float-right text-primary'
            onClick={() => setShowAddTrappingRunModal(true)}
          >
            {customAddTrappingRunOptions?.button.text
              ? customAddTrappingRunOptions?.button.text
              : 'Add Trapping Run'}
          </Button>
          <AddTrappingRunModal
            show={showAddTrappingRunModal}
            onHide={() => setShowAddTrappingRunModal(false)}
            addTrappingRun={addTrappingRun}
            customOptions={{
              autoGeneratePasscode:
                customAddTrappingRunOptions?.modal.autoGeneratePasscode,
              form: customAddTrappingRunOptions?.modal.form,
              header: customAddTrappingRunOptions?.modal.header,
              propertiesSelector: {
                display: customAddTrappingRunOptions?.modal.showPropertySelect,
                properties: customAddTrappingRunOptions?.modal.properties
              }
            }}
          />
        </Col>
      </Row>
      <Row className='mt-4'>
        <Col>
          <TrappingRunsTable
            trappingRuns={trappingRuns}
            changeSelectedTrappingRunId={props.changeSelectedTrappingRunId}
            deleteTrappingRun={deleteTrappingRun}
            customOptions={customTableOptions}
          />
        </Col>
      </Row>
    </Fragment>
  );
}

TrappingRuns.propTypes = {
  // Feature flags
  permissionsCard: PropTypes.bool,

  // Custom options
  customOptions: PropTypes.object,

  // Authentication
  selectedDatabase: PropTypes.string.isRequired,
  bearerToken: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,
  authService: PropTypes.string.isRequired,

  // Trapping Runs
  selectedTrappingRunId: PropTypes.number,
  changeSelectedTrappingRunId: PropTypes.func.isRequired
};

export default observer(TrappingRuns);
